if (!customElements.get('sadhana-book-shelf')) {
  customElements.define(
    'sadhana-book-shelf',
    class SadhanaBookShelf extends HTMLElement {
      constructor() {
        super();
        this.activeIndex = 0;
        this.dragState = null;
        this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      }

      connectedCallback() {
        this.triggers = Array.from(this.querySelectorAll('[data-book-trigger]'));
        this.details = Array.from(this.querySelectorAll('[data-book-detail]'));
        this.pairings = Array.from(this.querySelectorAll('[data-book-pairing]'));

        this.triggers.forEach((trigger) => {
          trigger.addEventListener('click', this.onTriggerClick);
          trigger.addEventListener('keydown', this.onTriggerKeydown);
          trigger.addEventListener('pointerdown', this.onPointerDown);
        });

        document.addEventListener('pointermove', this.onPointerMove);
        document.addEventListener('pointerup', this.onPointerUp);
        document.addEventListener('pointercancel', this.onPointerCancel);
      }

      disconnectedCallback() {
        this.triggers?.forEach((trigger) => {
          trigger.removeEventListener('click', this.onTriggerClick);
          trigger.removeEventListener('keydown', this.onTriggerKeydown);
          trigger.removeEventListener('pointerdown', this.onPointerDown);
        });

        document.removeEventListener('pointermove', this.onPointerMove);
        document.removeEventListener('pointerup', this.onPointerUp);
        document.removeEventListener('pointercancel', this.onPointerCancel);
      }

      onTriggerClick = (event) => {
        if (this.dragState?.wasDragging) {
          event.preventDefault();
          event.stopPropagation();
          this.dragState.wasDragging = false;
          return;
        }

        const trigger = event.currentTarget;
        this.selectBook(Number(trigger.dataset.bookIndex), trigger);
      };

      onTriggerKeydown = (event) => {
        const currentIndex = this.triggers.indexOf(event.currentTarget);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') {
          nextIndex = Math.min(this.triggers.length - 1, currentIndex + 1);
        } else if (event.key === 'ArrowLeft') {
          nextIndex = Math.max(0, currentIndex - 1);
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = this.triggers.length - 1;
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.selectBook(Number(event.currentTarget.dataset.bookIndex), event.currentTarget);
          return;
        } else {
          return;
        }

        event.preventDefault();
        this.triggers[nextIndex]?.focus();
        this.selectBook(Number(this.triggers[nextIndex].dataset.bookIndex), this.triggers[nextIndex]);
      };

      onPointerDown = (event) => {
        if (event.button !== 0 || event.pointerType === 'touch') return;

        const trigger = event.currentTarget;
        this.dragState = {
          trigger,
          startX: event.clientX,
          startY: event.clientY,
          currentX: event.clientX,
          currentY: event.clientY,
          pointerId: event.pointerId,
          ghost: null,
          isDragging: false,
          wasDragging: false
        };
      };

      onPointerMove = (event) => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        this.dragState.currentX = event.clientX;
        this.dragState.currentY = event.clientY;

        const movedX = Math.abs(event.clientX - this.dragState.startX);
        const movedY = Math.abs(event.clientY - this.dragState.startY);

        if (!this.dragState.isDragging && movedX + movedY > 8) {
          this.dragState.isDragging = true;
          this.dragState.wasDragging = true;
          this.dragState.ghost = this.createGhost(this.dragState.trigger, event.clientX, event.clientY);
        }

        if (this.dragState.ghost) {
          event.preventDefault();
          this.dragState.ghost.style.left = `${event.clientX}px`;
          this.dragState.ghost.style.top = `${event.clientY}px`;
        }
      };

      onPointerUp = (event) => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        const state = this.dragState;
        const index = Number(state.trigger.dataset.bookIndex);

        if (state.isDragging) {
          event.preventDefault();
          const selectedShelf = this.querySelector('[data-book-selected]');
          const dropRect = selectedShelf?.getBoundingClientRect();
          const droppedOnShelf = dropRect
            && event.clientX >= dropRect.left
            && event.clientX <= dropRect.right
            && event.clientY >= dropRect.top
            && event.clientY <= dropRect.bottom;

          this.selectBook(index, state.trigger, {
            fromPoint: droppedOnShelf ? { x: event.clientX, y: event.clientY } : null
          });
        }

        state.ghost?.remove();
        window.setTimeout(() => {
          if (this.dragState === state) this.dragState = null;
        }, 0);
      };

      onPointerCancel = (event) => {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        this.dragState.ghost?.remove();
        this.dragState = null;
      };

      selectBook(index, sourceTrigger, options = {}) {
        if (!Number.isFinite(index)) return;

        const targetDetail = this.details.find((detail) => Number(detail.dataset.bookIndex) === index);
        const targetPairing = this.pairings.find((pairing) => Number(pairing.dataset.bookIndex) === index);
        if (!targetDetail) return;

        this.activeIndex = index;

        this.triggers.forEach((trigger) => {
          const isActive = Number(trigger.dataset.bookIndex) === index;
          trigger.classList.toggle('is-active', isActive);
          trigger.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        sourceTrigger?.scrollIntoView({
          behavior: this.prefersReducedMotion.matches ? 'auto' : 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });

        const showContent = () => {
          this.showPanel(this.details, targetDetail);
          if (targetPairing) this.showPanel(this.pairings, targetPairing);
        };

        if (this.prefersReducedMotion.matches || !sourceTrigger) {
          showContent();
          return;
        }

        this.animateBook(sourceTrigger, targetDetail, options).then(showContent);
      }

      showPanel(panels, targetPanel) {
        panels.forEach((panel) => {
          const isActive = panel === targetPanel;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
          panel.classList.remove('is-entering');
        });

        targetPanel.classList.add('is-entering');
        window.setTimeout(() => targetPanel.classList.remove('is-entering'), 320);
      }

      createGhost(trigger, x, y) {
        const cover = trigger.querySelector('.sadhana-book-shelf__book-cover');
        if (!cover) return null;

        const rect = cover.getBoundingClientRect();
        const ghost = cover.cloneNode(true);
        ghost.classList.add('sadhana-book-shelf__drag-ghost');
        ghost.style.setProperty('--sbs-drag-width', `${rect.width}px`);
        ghost.style.left = `${x}px`;
        ghost.style.top = `${y}px`;
        document.body.appendChild(ghost);
        return ghost;
      }

      animateBook(sourceTrigger, targetDetail, options = {}) {
        const sourceCover = sourceTrigger.querySelector('.sadhana-book-shelf__book-cover');
        const visibleDetail = this.details.find((detail) => !detail.hidden) || targetDetail;
        const targetImage = visibleDetail.querySelector('[data-book-detail-image]');

        if (!sourceCover || !targetImage) return Promise.resolve();

        const sourceRect = sourceCover.getBoundingClientRect();
        const targetRect = targetImage.getBoundingClientRect();
        const startX = options.fromPoint ? options.fromPoint.x - sourceRect.width / 2 : sourceRect.left;
        const startY = options.fromPoint ? options.fromPoint.y - sourceRect.height / 2 : sourceRect.top;
        const scaleX = targetRect.width / sourceRect.width;
        const scaleY = targetRect.height / sourceRect.height;
        const scale = Math.min(scaleX, scaleY, 1.4);
        const targetX = targetRect.left - startX;
        const targetY = targetRect.top - startY;
        const lift = Math.min(110, Math.max(46, Math.abs(targetY) * 0.32));

        const flyingBook = sourceCover.cloneNode(true);
        flyingBook.classList.add('sadhana-book-shelf__flying-book');
        flyingBook.style.setProperty('--sbs-fly-width', `${sourceRect.width}px`);
        flyingBook.style.left = `${startX}px`;
        flyingBook.style.top = `${startY}px`;
        document.body.appendChild(flyingBook);

        return new Promise((resolve) => {
          const animation = flyingBook.animate(
            [
              {
                opacity: 0.98,
                transform: 'translate3d(0, 0, 0) rotate(-5deg) scale(1)'
              },
              {
                opacity: 1,
                transform: `translate3d(${targetX * 0.46}px, ${targetY * 0.44 - lift}px, 0) rotate(3deg) scale(${Math.min(scale * 1.08, 1.28)})`
              },
              {
                opacity: 0.16,
                transform: `translate3d(${targetX}px, ${targetY}px, 0) rotate(0deg) scale(${scale})`
              }
            ],
            {
              duration: 680,
              easing: 'cubic-bezier(0.2, 0.9, 0.22, 1)',
              fill: 'forwards'
            }
          );

          animation.addEventListener('finish', () => {
            flyingBook.remove();
            resolve();
          });
        });
      }
    }
  );
}
